import React, { useEffect, useRef, useState } from 'react';

const CANVAS_W = 1772;
const CANVAS_H = 3189;
const PREVIEW_W = 360; // 미리보기 너비

// PSD에서 추출한 기준선 좌표 (포토샵 가이드)
const GUIDES_V = [175.03, 1603.13];
const GUIDES_H = [517.19, 826.78, 1716.13, 2080];

// 단락 정렬 모드
type Align = 'auto' | 'left' | 'justify';
interface Paragraph { text: string; align: Align; }

// 앞면 기본값 (JPG 기준 좌표·폰트)
const FRONT_DEFAULTS = {
  smallTitle: '남해로부터',
  bigTitle: '거제 맹종죽순',
  paragraphs: [
    {
      text: `거제 하청면 뒷산, 50년 대나무 외길 옥무근 농부님은 오늘도
새벽빛이 채 오르기 전에 대밭에 올라요. 4월이 되면 새벽 이슬로
여기저기서 죽순이 쑥쑥~ 올라오거든요. 질기지 않도록 30cm 이하
어린 죽순만 골라 수확하고, 바로 손질하면 아린맛이 없고
부드러워요. 따뜻한 남해안의 해풍이 불어와 수분과 미네랄을
머금은 황토로 인해, 거제에서 자란 죽순은 더 연하고 더
촉촉하답니다. 겉 껍질을 벗기면 은은한 옥수수 향이 올라오는데,
그게 봄이 왔다는 신호예요.`,
      align: 'auto' as Align,
    },
    {
      text: '남해안에서 전하는 봄 소식으로 이 계절을 천천히 즐겨 보세요.',
      align: 'left' as Align,
    },
  ] as Paragraph[],
};

// 뒷면 기본값 — 단락 자유 입력
const BACK_DEFAULTS: { paragraphs: Paragraph[]; customerCenter: string } = {
  paragraphs: [
    { text: '【보관 방법】', align: 'left' },
    { text: '· 생죽순: 받으시자마자 바로 껍질 제거 후 삶아 보관해 주세요. 시간이 지날수록 질겨져요.', align: 'auto' },
    { text: '· 냉장: 죽순이 잠길 정도로 물을 넣어 용기에 보관하고 물을 수시로 갈아주세요. 2~3일 이내 드세요', align: 'auto' },
    { text: '· 냉동: 4일 이상 못 드실 경우 썰어서 냉동 보관하세요. 진공포장하면 더욱 좋아요.', align: 'auto' },
    { text: '', align: 'left' },
    { text: '【생죽순 손질법】', align: 'left' },
    { text: '· 칼로 세로로 반 갈라 껍질을 벗기시고, 뽀얀 속살만 남으면 돼요.', align: 'left' },
    { text: '· 물 또는 쌀뜨물에 1~2시간 삶아주세요. 옥수수 향이 나면 잘 삶아진 거예요.', align: 'auto' },
    { text: '· 찬물에 헹군 뒤 반나절 이상 물을 갈아가며 담가두면 아린맛이 빠져요.', align: 'left' },
    { text: '', align: 'left' },
    { text: '【혹시 상품이나 배송에 문제가 있나요?】', align: 'left' },
    { text: '삶은 죽순의 하얀 가루는 단백질(티로신)이 응결된 것으로 물에 헹구면 사라져요.', align: 'auto' },
    { text: '꼼꼼하게 챙겨서 보내드렸지만, 이상이 있다면 받으신 직후 사진과 함께 고객센터로 연락 주세요.', align: 'auto' },
    { text: '', align: 'left' },
    { text: '*재사용이 가능한 야채 봉투예요. 나눔 봉투나 채소·과일 보관용으로 활용해 보세요.', align: 'auto' },
  ],
  customerCenter: '남해로부터 고객센터',
};

type Side = 'front' | 'back';

interface FrontData {
  smallTitle: string;
  bigTitle: string;
  paragraphs: Paragraph[];
}
interface BackData {
  paragraphs: Paragraph[];
  customerCenter: string;
  qrDataUrl: string | null;
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });
}

// 줄바꿈 유지하며 캔버스에 텍스트 그리기 (단순 left-align)
function drawMultiline(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  lineHeight: number,
) {
  const lines = text.split('\n');
  lines.forEach((line, i) => ctx.fillText(line, x, y + i * lineHeight));
}

// 한 줄을 targetWidth에 맞춰 글자 단위로 wrap한 배열 반환
function wrapLineToWidth(ctx: CanvasRenderingContext2D, line: string, targetWidth: number): string[] {
  if (!line) return [''];
  if (ctx.measureText(line).width <= targetWidth) return [line];
  const out: string[] = [];
  let cur = '';
  for (const ch of Array.from(line)) {
    const test = cur + ch;
    if (ctx.measureText(test).width > targetWidth) {
      if (cur) out.push(cur);
      cur = ch;
    } else {
      cur = test;
    }
  }
  if (cur) out.push(cur);
  return out;
}

// 양쪽 정렬(justify) 텍스트 그리기. 단락 마지막 줄(원본 기준)은 강제 왼쪽 정렬.
// targetWidth보다 긴 줄은 자동 wrap. wrap된 줄들 중 원본 마지막 줄에서 wrap된 마지막 표시줄만 왼쪽 정렬.
function drawJustified(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  lineHeight: number,
  targetWidth: number,
): number {
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'left';
  const sourceLines = text.split('\n');
  // wrap 후 표시 라인 목록 (마지막 여부 표시)
  type DispLine = { text: string; isLastDisplayOfParagraph: boolean };
  const display: DispLine[] = [];
  sourceLines.forEach((line, i) => {
    if (!line.trim()) { display.push({ text: '', isLastDisplayOfParagraph: false }); return; }
    const next = sourceLines[i + 1];
    const isParaEnd = i === sourceLines.length - 1 || (next !== undefined && next.trim() === '');
    const wrapped = wrapLineToWidth(ctx, line, targetWidth);
    wrapped.forEach((w, wi) => {
      display.push({ text: w, isLastDisplayOfParagraph: isParaEnd && wi === wrapped.length - 1 });
    });
  });
  display.forEach((d, idx) => {
    const cy = y + idx * lineHeight;
    if (!d.text) return;
    const natural = ctx.measureText(d.text).width;
    if (d.isLastDisplayOfParagraph || natural >= targetWidth) {
      ctx.fillText(d.text, x, cy);
    } else {
      const chars = Array.from(d.text);
      const extra = targetWidth - natural;
      const gap = chars.length > 1 ? extra / (chars.length - 1) : 0;
      let cx = x;
      for (const ch of chars) {
        ctx.fillText(ch, cx, cy);
        cx += ctx.measureText(ch).width + gap;
      }
    }
  });
  ctx.textAlign = prevAlign;
  return display.length;
}

const FONT_FAMILY_SERIF = `'Batang', 'Nanum Myeongjo', 'NanumMyeongjo', serif`;

// 단락 단위 렌더링. 빈 줄 한 줄 높이로 단락 사이 간격.
function renderParagraphs(
  ctx: CanvasRenderingContext2D,
  paras: Paragraph[],
  x: number,
  startY: number,
  lineHeight: number,
  targetWidth: number,
  paragraphSpacing = 0,
): number {
  let y = startY;
  paras.forEach((p, idx) => {
    if (!p.text.trim()) {
      y += lineHeight;
      return;
    }
    if (p.align === 'left') {
      const sourceLines = p.text.split('\n');
      sourceLines.forEach((line) => {
        const wrapped = line.trim() ? wrapLineToWidth(ctx, line, targetWidth) : [''];
        wrapped.forEach((w) => { ctx.fillText(w, x, y); y += lineHeight; });
      });
    } else if (p.align === 'justify') {
      // 모든 줄을 양쪽 정렬 (wrap 포함, 마지막 줄도 양쪽)
      const sourceLines = p.text.split('\n');
      sourceLines.forEach((line) => {
        const wrapped = line.trim() ? wrapLineToWidth(ctx, line, targetWidth) : [''];
        wrapped.forEach((w) => {
          const natural = ctx.measureText(w).width;
          if (w && natural < targetWidth) {
            const chars = Array.from(w);
            const extra = targetWidth - natural;
            const gap = chars.length > 1 ? extra / (chars.length - 1) : 0;
            let cx = x;
            for (const ch of chars) { ctx.fillText(ch, cx, y); cx += ctx.measureText(ch).width + gap; }
          } else {
            ctx.fillText(w, x, y);
          }
          y += lineHeight;
        });
      });
    } else {
      // auto: 마지막 줄 외 양쪽 정렬, 마지막 줄은 왼쪽
      const rows = drawJustified(ctx, p.text, x, y, lineHeight, targetWidth);
      y += rows * lineHeight;
    }
    if (idx < paras.length - 1) y += paragraphSpacing;
  });
  return y;
}

function drawGuides(ctx: CanvasRenderingContext2D) {
  ctx.save();
  ctx.strokeStyle = '#33d6ff';
  ctx.lineWidth = 2;
  GUIDES_V.forEach((x) => {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, CANVAS_H); ctx.stroke();
  });
  GUIDES_H.forEach((y) => {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(CANVAS_W, y); ctx.stroke();
  });
  // 좌표 라벨 (미리보기 디버깅용)
  ctx.fillStyle = '#0fa3c4';
  ctx.font = '24px sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  GUIDES_V.forEach((x) => {
    ctx.fillText(`x=${Math.round(x)}`, x + 6, 8);
  });
  GUIDES_H.forEach((y) => {
    ctx.fillText(`y=${Math.round(y)}`, 8, y + 4);
  });
  ctx.restore();
}

function renderFront(canvas: HTMLCanvasElement, data: FrontData) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'alphabetic';

  // 원본 JPG 실측 기준
  // 작은 제목: 텍스트 영역 y=611~706 (block 96px) → 가운데 정렬 baseline ~y=696
  ctx.font = `80px ${FONT_FAMILY_SERIF}`;
  ctx.textAlign = 'center';
  ctx.fillText(data.smallTitle, CANVAS_W / 2, 696);

  // 큰 제목: 텍스트 영역 y=752~870 (block 119px) → baseline ~y=854, 볼드 처리
  ctx.font = `bold 108px ${FONT_FAMILY_SERIF}`;
  ctx.textAlign = 'center';
  ctx.fillText(data.bigTitle, CANVAS_W / 2, 854);

  // 본문: 텍스트 영역 y=1364~2011 (9줄, 줄당 72px), 좌측 x=175 / 우측 x=1603
  ctx.font = `48px ${FONT_FAMILY_SERIF}`;
  ctx.textAlign = 'left';
  const bodyX = 175;
  const bodyTargetWidth = 1603 - 175; // 1428
  renderParagraphs(ctx, data.paragraphs, bodyX, 1414, 72, bodyTargetWidth, 0);
}

function renderBack(canvas: HTMLCanvasElement, data: BackData, qrImg?: HTMLImageElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  // 원본 JPG 실측 기준: 좌우 여백 x=175~1603
  const bodyX = 175;
  const targetWidth = 1603 - 175; // 1428
  const bodyFontSize = 44; // 1폰트 크게
  const bodyLineHeight = 70;

  // 본문 단락 자유 입력 — y=560 시작 (원본 첫 텍스트 ~y=515 + ascent 44)
  // 단락 사이 40px 여백으로 시각적 구분
  ctx.font = `${bodyFontSize}px ${FONT_FAMILY_SERIF}`;
  ctx.textAlign = 'left';
  const finalY = renderParagraphs(ctx, data.paragraphs, bodyX, 560, bodyLineHeight, targetWidth, 40);

  // 고객센터 + QR (오른쪽 하단, 본문 마지막 다음에 위치)
  // 본문이 짧으면 본문 직후, 길면 캔버스 하단 가까이
  const baseY = Math.max(finalY + 30, 2076);
  const ccFontSize = bodyFontSize + 8; // 본문보다 2폰트 크게
  ctx.font = `${ccFontSize}px ${FONT_FAMILY_SERIF}`;
  ctx.textAlign = 'right';
  const ccX = qrImg ? 1603 - 220 : 1603;
  ctx.fillText(data.customerCenter, ccX, baseY);
  if (qrImg) {
    const qrSize = 200;
    ctx.drawImage(qrImg, 1603 - qrSize, baseY - qrSize + 20, qrSize, qrSize);
  }
}

// 단락 정렬 에디터 — 단락 추가/삭제/순서, 각 단락에 정렬 선택
function ParagraphEditor({ paragraphs, onChange }: { paragraphs: Paragraph[]; onChange: (p: Paragraph[]) => void }) {
  const update = (idx: number, patch: Partial<Paragraph>) => {
    const next = [...paragraphs];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx: number) => onChange(paragraphs.filter((_, i) => i !== idx));
  const add = () => onChange([...paragraphs, { text: '', align: 'auto' }]);
  const move = (idx: number, dir: -1 | 1) => {
    const t = idx + dir;
    if (t < 0 || t >= paragraphs.length) return;
    const next = [...paragraphs];
    [next[idx], next[t]] = [next[t], next[idx]];
    onChange(next);
  };
  const inputCls = 'w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-jeju-brown';
  return (
    <div className="space-y-2">
      {paragraphs.map((p, i) => (
        <div key={i} className="bg-gray-50 rounded p-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-400 font-mono">¶{i + 1}</span>
            <select
              value={p.align}
              onChange={(e) => update(i, { align: e.target.value as Align })}
              className="px-2 py-1 border border-gray-200 rounded text-xs"
              title="단락 정렬"
            >
              <option value="auto">자동 (양쪽, 마지막줄 왼쪽)</option>
              <option value="left">왼쪽 정렬</option>
              <option value="justify">양쪽 정렬 (모두)</option>
            </select>
            <div className="ml-auto flex gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="px-1.5 py-0.5 text-[11px] border border-gray-200 rounded text-gray-500 disabled:opacity-30">↑</button>
              <button onClick={() => move(i, 1)} disabled={i === paragraphs.length - 1} className="px-1.5 py-0.5 text-[11px] border border-gray-200 rounded text-gray-500 disabled:opacity-30">↓</button>
              <button onClick={() => remove(i)} className="px-1.5 py-0.5 text-[11px] text-red-400 hover:text-red-600">삭제</button>
            </div>
          </div>
          <textarea
            value={p.text}
            onChange={(e) => update(i, { text: e.target.value })}
            rows={Math.max(2, Math.min(8, p.text.split('\n').length + 1))}
            className={`${inputCls} leading-relaxed font-sans`}
            placeholder="단락 내용 (줄바꿈 가능)"
          />
        </div>
      ))}
      <button
        onClick={add}
        className="w-full py-1.5 border border-dashed border-gray-300 text-[11px] text-gray-500 rounded hover:bg-gray-50"
      >+ 단락 추가</button>
    </div>
  );
}

export default function EnvelopeDesigner() {
  const [side, setSide] = useState<Side>('front');
  const [showGuides, setShowGuides] = useState(true);
  const [front, setFront] = useState<FrontData>(FRONT_DEFAULTS);
  const [back, setBack] = useState<BackData>({
    paragraphs: BACK_DEFAULTS.paragraphs.map((p) => ({ ...p })),
    customerCenter: BACK_DEFAULTS.customerCenter,
    qrDataUrl: null,
  });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const qrImgRef = useRef<HTMLImageElement | null>(null);

  // QR 이미지 로드
  useEffect(() => {
    if (!back.qrDataUrl) { qrImgRef.current = null; redraw(); return; }
    const img = new Image();
    img.onload = () => { qrImgRef.current = img; redraw(); };
    img.src = back.qrDataUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [back.qrDataUrl]);

  function renderTo(c: HTMLCanvasElement, withGuides: boolean) {
    if (side === 'front') renderFront(c, front);
    else renderBack(c, back, qrImgRef.current || undefined);
    if (withGuides) {
      const ctx = c.getContext('2d');
      if (ctx) drawGuides(ctx);
    }
  }

  function redraw() {
    const c = canvasRef.current;
    if (!c) return;
    renderTo(c, showGuides);
  }

  useEffect(() => { redraw(); }, [side, front, back, showGuides]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleQrUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = await fileToDataUrl(f);
    setBack({ ...back, qrDataUrl: url });
  }

  function downloadJpg() {
    const c = canvasRef.current;
    if (!c) return;
    // 가이드 없이 렌더 후 다운로드 → 다시 미리보기 복원
    renderTo(c, false);
    const url = c.toDataURL('image/jpeg', 0.95);
    const filename = side === 'front' ? '나눔봉투_앞면.jpg' : '나눔봉투_뒷면.jpg';
    downloadDataUrl(url, filename);
    redraw();
  }

  function downloadBoth() {
    const c = canvasRef.current;
    if (!c) return;
    // 앞면 (가이드 없이) → 다운로드
    renderFront(c, front);
    downloadDataUrl(c.toDataURL('image/jpeg', 0.95), '나눔봉투_앞면.jpg');
    // 뒷면 (가이드 없이) → 다운로드
    renderBack(c, back, qrImgRef.current || undefined);
    downloadDataUrl(c.toDataURL('image/jpeg', 0.95), '나눔봉투_뒷면.jpg');
    // 현재 뷰 복원
    redraw();
  }

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown';

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">나눔 봉투</h1>

      <div className="flex gap-6">
        {/* 입력 패널 */}
        <div className="w-[480px] space-y-4 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setSide('front')}
                className={`flex-1 px-4 py-2 rounded text-sm font-medium ${side === 'front' ? 'bg-jeju-brown text-white' : 'bg-gray-100 text-gray-600'}`}
              >앞면</button>
              <button
                onClick={() => setSide('back')}
                className={`flex-1 px-4 py-2 rounded text-sm font-medium ${side === 'back' ? 'bg-jeju-brown text-white' : 'bg-gray-100 text-gray-600'}`}
              >뒷면</button>
            </div>

            {side === 'front' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">작은 제목</label>
                  <input value={front.smallTitle} onChange={(e) => setFront({ ...front, smallTitle: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">큰 제목</label>
                  <input value={front.bigTitle} onChange={(e) => setFront({ ...front, bigTitle: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-2">본문 (단락별 정렬 선택)</label>
                  <ParagraphEditor
                    paragraphs={front.paragraphs}
                    onChange={(paragraphs) => setFront({ ...front, paragraphs })}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-2">본문 (단락별 정렬 선택)</label>
                  <ParagraphEditor
                    paragraphs={back.paragraphs}
                    onChange={(paragraphs) => setBack({ ...back, paragraphs })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">고객센터 라벨 (오른쪽 하단)</label>
                  <input value={back.customerCenter} onChange={(e) => setBack({ ...back, customerCenter: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">QR 이미지 (선택)</label>
                  <input type="file" accept="image/*" onChange={handleQrUpload} className="text-xs" />
                  {back.qrDataUrl && (
                    <button
                      onClick={() => setBack({ ...back, qrDataUrl: null })}
                      className="ml-2 text-xs text-red-400 hover:text-red-600"
                    >제거</button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-1">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showGuides}
                onChange={(e) => setShowGuides(e.target.checked)}
                className="accent-jeju-brown"
              />
              기준선 표시 (PSD 가이드)
            </label>
          </div>

          <div className="flex gap-2">
            <button onClick={downloadJpg} className="flex-1 px-4 py-2 bg-jeju-brown text-white rounded font-medium">현재 면 JPG 저장</button>
            <button onClick={downloadBoth} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded">앞·뒷 모두 저장</button>
          </div>

          <p className="text-xs text-gray-400 leading-relaxed">
            폰트는 시스템의 Batang/NanumMyeongjo가 사용됩니다. 기준선은 PSD에서 추출한 가이드(세로 175·1603 / 가로 517·827·1716·2080)이며 JPG 내보내기 시엔 제외됩니다.
          </p>
        </div>

        {/* 미리보기 */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-center">
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            style={{ width: PREVIEW_W, height: PREVIEW_W * (CANVAS_H / CANVAS_W), border: '1px solid #e5e7eb' }}
          />
        </div>
      </div>
    </div>
  );
}
